/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  downloadDocxFile,
  downloadDocxTemplate,
  downloadPdfFile,
  downloadPdfTemplate,
  getReviewerInfo,
  getReviewDetail,
} from 'apis/acceptanceReviews.api';

import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';

import { DetailInfo } from 'features/FinancialReview/components';
import { EvaluationForm, EvaluateTable } from 'features/AcceptanceReview/components';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { HugerteEditor } from 'components/FormControl';

import { getAcadamicLevelShortHand, is1593, toNonAccentVietnamese } from 'utils/func';
import { TabPanel, TabView } from 'primereact/tabview';
import { ProjectProposalDefenseDetail } from 'features/ProjectProposalDefense/pages';
import { getSummaryReport, downloadFileReport } from 'apis/summaryReport';
import ReviewFormMidtermAcceptance from 'features/Project/components/ReviewFormMidtermAcceptance';
import Signature from 'features/PrequalificationReview/components/Signature';
import DynamicAcceptanceForm from 'features/AcceptanceReview/components/DynamicAcceptanceReview';
import GeneralCouncilInfo from 'features/Council/components/GeneralInfo';

export default function AcceptanceReviewDetail() {
  // #region Data
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();

  const { projectProposalId } = useParams();
  const [searchParams] = useSearchParams();
  const councilId = searchParams.get('councilId');
  const councilMemberId = searchParams.get('councilMemberId');
  const downloadButtonRef = useRef();
  const downloadTemplateButtonRef = useRef();
  const summaryReportId = searchParams.get('summaryReportId');

  const location = useLocation();
  const { isViewCouncilData } = location?.state || { isViewCouncilData: false };

  // A. General Information
  const {
    data: infoData,
    isLoading,
    refetch: refetchReviewerInfo,
  } = useQuery(['acceptance-reviewer', projectProposalId, councilId], () =>
    getReviewerInfo(projectProposalId, councilId)
  );
  const info = useMemo(() => infoData?.data, [infoData]);

  const { data: acceptanceReviewData, isLoading: isLoadingAcceptanceReview } = useQuery(
    ['acceptance-review', councilMemberId, councilId, projectProposalId],
    () => getReviewDetail(councilMemberId, projectProposalId, councilId),
    { enabled: !isLoading && !is1593(info?.projectProposal?.template) }
  );
  const acceptanceReview = useMemo(() => acceptanceReviewData?.data, [acceptanceReviewData]);

  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const { ...formOptions } = useForm({
    mode: 'onChange',
  });

  const { data: _summaryReport } = useQuery(
    ['midtermSummaryReport', summaryReportId],
    () => getSummaryReport(summaryReportId),
    {
      enabled: summaryReportId !== null,
    }
  );

  const summaryReport = useMemo(() => _summaryReport?.data, [_summaryReport]);

  const { mutate: mutateDownloadFile } = useMutation(downloadFileReport);

  const handleDownloadFile = (filename, fileNameFormat = '') => {
    mutateDownloadFile(
      {
        summaryReportId,
        filename,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = fileNameFormat || filename;

            link.click();
          }
        },
      }
    );
  };

  // Fill available data to form
  useEffect(() => {
    if (acceptanceReview?.acceptanceReview) {
      const { acceptanceReview: review } = acceptanceReview;
      const {
        scoreTarget,
        scoreContent,
        scoreApproach,
        scoreProcess,
        scoreApplicationProduct,
        scorePublishedProduct,
        scoreTrainingProduct,
        scoreReportQuality,
      } = review;
      const scoreResponse = parseInt(scoreTarget + scoreContent + scoreApproach + scoreProcess, 10);
      const scoreQuantityAndQuality = parseInt(
        scoreApplicationProduct + scorePublishedProduct + scoreTrainingProduct,
        10
      );
      setValue('reviewMeet', review.reviewMeet);
      setValue('reviewProduct', review.reviewProduct);
      setValue('reviewReportQuality', review.reviewReportQuality);
      setValue('reviewConclusion', review.reviewConclusion);
      setValue('score.scoreApproach', review.scoreApproach);
      setValue('score.scoreTarget', scoreTarget);
      setValue('score.scoreContent', scoreContent);
      setValue('score.scoreProcess', scoreProcess);
      setValue('score.scoreResponse', scoreResponse);
      setValue('score.scoreApplicationProduct', scoreApplicationProduct);
      setValue('score.scorePublishedProduct', scorePublishedProduct);
      setValue('score.scoreTrainingProduct', scoreTrainingProduct);
      setValue('score.scoreQuantityAndQuality', scoreQuantityAndQuality);
      setValue('score.scoreReportQuality', scoreReportQuality);

      reset({}, { keepValues: true });
    }
  }, [acceptanceReview, acceptanceReview?.acceptanceReview, setValue]);

  // #endregion

  // #region Events

  const {
    refetch: handleDownloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useQuery(
    ['downloadPdfFileAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () => downloadPdfFile({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const academicLevelShorthand = getAcadamicLevelShortHand(
            info?.user?.scientificProfile?.academicRank,
            info?.user?.scientificProfile?.academicDegree,
            t,
            true
          );
          const filename = `Phieu nhan xet danh gia nghiem thu - ${academicLevelShorthand}${toNonAccentVietnamese(
            info?.user?.fullname
          )}.pdf`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: (error) => {
        // 490 is the status code for the error
        // when the scientist has not reviewed the online form yet
        if (error?.response?.status === 490) {
          toast(TOAST.ERROR, t('errorMessage.scientistNotReviewOnlineFormYet'));
        } else {
          toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
        }
      },
    }
  );

  const {
    refetch: handleDownloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useQuery(
    ['downloadDocxFileAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () => downloadDocxFile({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const academicLevelShorthand = getAcadamicLevelShortHand(
            info?.user?.scientificProfile?.academicRank,
            info?.user?.scientificProfile?.academicDegree,
            t,
            true
          );
          const filename = `Phieu nhan xet danh gia nghiem thu - ${academicLevelShorthand}${toNonAccentVietnamese(
            info?.user?.fullname
          )}.docx`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: (error) => {
        // 490 is the status code for the error
        // when the scientist has not reviewed the online form yet
        if (error?.response?.status === 490) {
          toast(TOAST.ERROR, t('errorMessage.scientistNotReviewOnlineFormYet'));
        } else {
          toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
        }
      },
    }
  );

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () => downloadPdfTemplate({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () => downloadDocxTemplate({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const renderFirstTab = () => {
    if (is1593(info?.projectProposal?.template) || isLoading) {
      return (
        <DynamicAcceptanceForm
          projectProposalId={projectProposalId}
          councilId={councilId}
          councilMemberId={councilMemberId}
          generalInfo={info?.projectProposal}
          isLoading={isLoading}
          readOnly
          formOptions={formOptions}
        />
      );
    }
    return (
      <div className="m-0 bg-white px-4 sm:px-4 md:px-4 lg:px-6 xl:px-8 mb-3 py-5 p-fluid relative financial-review-edit">
        <div className="flex gap-1 justify-content-end">
          <Button
            label={t('council.review.exportFile')}
            icon={`pi ${
              isDownloadPdfFileLoading ||
              isDownloadPdfFileFetching ||
              isDownloadDocxFileLoading ||
              isDownloadDocxFileFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadButtonRef.current.toggle(event)}
            disabled={
              isDownloadPdfFileLoading ||
              isDownloadPdfFileFetching ||
              isDownloadDocxFileLoading ||
              isDownloadDocxFileFetching
            }
          />
          <Button
            // type="button"
            label={t('council.review.downloadTemplate')}
            icon={`pi ${
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
            disabled={
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
            }
          />
        </div>
        <Menu
          ref={downloadButtonRef}
          model={[
            {
              label: t('council.review.exportPdf'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfFile,
              disabled: isDownloadPdfFileLoading || isDownloadPdfFileFetching,
            },
            {
              label: t('council.review.exportDocx'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxFile,
              disabled: isDownloadDocxFileLoading || isDownloadDocxFileFetching,
            },
          ]}
          popup
        />
        <Menu
          ref={downloadTemplateButtonRef}
          model={[
            {
              label: t('council.review.downloadPdfTemplate'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfTemplate,
              disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
            },
            {
              label: t('council.review.downloadDocxTemplate'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxTemplate,
              disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
            },
          ]}
          popup
        />

        <div className="mb-5 flex justify-content-between align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="VNU-HCM logo"
            className="ml-3 h-4rem md:h-5rem"
          />
          <div className="pl-2">
            {t('projectProposal.viewDetail.form')} M08 <br />
            {t('council.m08.projectCode')}
          </div>
        </div>
        <div className="text-center mb-6">
          <h3 className="font-bold mb-0">{t('council.m08.title')}</h3>
          <h3 className="mt-0">{t('council.m08.sciTechProject')}</h3>
        </div>

        <DetailInfo info={info} />

        <form autoComplete="off">
          <EvaluationForm
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            control={control}
            errors={errors}
            readOnly
          />

          <EvaluateTable
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            control={control}
            errors={errors}
            researchType={info?.projectProposal?.researchType}
            readOnly
          />

          <h5 className="font-bold">{t('council.m08.b.rank.label')}</h5>
          <ul>
            <li>{t('council.m08.b.rank.excellent')}</li>
            <li>{t('council.m08.b.rank.veryGood')}</li>
            <li>{t('council.m08.b.rank.good')}</li>
            <li>{t('council.m08.b.rank.reached')}</li>
            <li>{t('council.m08.b.rank.notReached')}</li>
          </ul>
          <div className="text-lg mt-3">
            <h5>{t('council.m08.c.conclusion')}</h5>
            <HugerteEditor name="reviewConclusion" control={control} errors={errors} readOnly />
          </div>
          <div className="grid text-lg mt-3">
            <div className="col-7 px-8">
              <span>{t('council.m01.c.commitment')}</span>
            </div>

            <div className="col-5 text-center">
              <p className="mb-0">{t('signature.date')}</p>
              <p className="font-semibold mb-0">{t('signature.reviewer')}</p>
              <p>{t('signature.fullnameAndSignature')}</p>
              <Signature refetchReviewerInfo={refetchReviewerInfo} reviewerInfo={info?.user} />
            </div>
          </div>

          <div className="flex mt-4 justify-content-end">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              className="w-8rem"
              onClick={() => navigate('/project-proposal-defense')}
            />
          </div>
        </form>
      </div>
    );
  };
  // #endregion

  return (
    <>
      {isLoadingAcceptanceReview && <Loading />}

      <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
        <TabPanel header={t('council.review.createOrUpdate')} leftIcon="pi pi-file mr-2">
          {renderFirstTab()}
        </TabPanel>
        <TabPanel header={t('council.review.detailProjectProposal')} leftIcon="pi pi-list mr-2">
          <ProjectProposalDefenseDetail />
        </TabPanel>
        <TabPanel header="Nghiệm thu" leftIcon="pi pi-list mr-2">
          <ReviewFormMidtermAcceptance
            summaryReport={summaryReport}
            handleDownloadFile={handleDownloadFile}
            control={control}
            errors={errors}
          />
        </TabPanel>
        {isViewCouncilData && (
          <TabPanel header={t('council.review.councilInfo')} leftIcon="pi pi-info-circle mr-2">
            <GeneralCouncilInfo councilId={councilId} projectProposalId={projectProposalId} />
          </TabPanel>
        )}
      </TabView>
    </>
  );
}
